import React, { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-draw/dist/leaflet.draw.css";
import "leaflet-draw";
import "./vegi.css";

import icon from "leaflet/dist/images/marker-icon.png";
import iconShadow from "leaflet/dist/images/marker-shadow.png";

let DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconAnchor: [12, 41],
});
L.Marker.prototype.options.icon = DefaultIcon;

const API_BASE_URL = "https://sadaatagrofarms.com/api/voice_api";
const BASE_URL = "https://sadaatagrofarms.com";

const LANGUAGES = [
  { code: "ur", label: "اردو", flag: "🇵🇰" },
  { code: "en", label: "English", flag: "🇬🇧" },
  { code: "ar", label: "العربية", flag: "🇸🇦" },
  { code: "zh", label: "中文", flag: "🇨🇳" },
  { code: "fr", label: "Français", flag: "🇫🇷" },
  { code: "de", label: "Deutsch", flag: "🇩🇪" },
  { code: "hi", label: "हिन्दी", flag: "🇮🇳" },
  { code: "tr", label: "Türkçe", flag: "🇹🇷" },
];

const VegetationAnalysis = () => {
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const drawnItemsRef = useRef(null);
  const indexImageLayerRef = useRef(null);
  const audioRef = useRef(null);

  const [selectedIndex, setSelectedIndex] = useState("ndvi");
  const [currentBbox, setCurrentBbox] = useState(null);
  const [syncData, setSyncData] = useState([]);
  const [currentWordIndex, setCurrentWordIndex] = useState(-1);
  const [status, setStatus] = useState({ message: "", type: "info" });
  const [resultsVisible, setResultsVisible] = useState(false);
  const [audioUrl, setAudioUrl] = useState("");
  const [indexData, setIndexData] = useState(null);

  const [farmers, setFarmers] = useState([]);
  const [farms, setFarms] = useState([]);
  const [selectedFarmer, setSelectedFarmer] = useState(null);
  const [selectedFarm, setSelectedFarm] = useState(null);
  const [farmerDropdownOpen, setFarmerDropdownOpen] = useState(false);
  const [farmDropdownOpen, setFarmDropdownOpen] = useState(false);

  // Language for the NDVI report voice + text
  const [selectedLang, setSelectedLang] = useState("ur");
  const [translating, setTranslating] = useState(false);

  // -------------------------------------------------------
  // MAP INIT
  // -------------------------------------------------------
  useEffect(() => {
    const map = L.map(mapRef.current).setView([33.6844, 73.0479], 13);
    mapInstance.current = map;
    drawnItemsRef.current = new L.FeatureGroup();
    map.addLayer(drawnItemsRef.current);

    L.tileLayer("https://{s}.google.com/vt/lyrs=s,h&x={x}&y={y}&z={z}", {
      maxZoom: 20,
      minZoom: 4,
      subdomains: ["mt0", "mt1", "mt2", "mt3"],
    }).addTo(map);

    const drawControl = new L.Control.Draw({
      draw: {
        polygon: false,
        circle: false,
        marker: false,
        polyline: false,
        circlemarker: false,
        rectangle: { shapeOptions: { color: "#00ff88", weight: 3 } },
      },
      edit: { featureGroup: drawnItemsRef.current },
    });
    map.addControl(drawControl);

    map.on(L.Draw.Event.CREATED, (e) => {
      drawnItemsRef.current.clearLayers();
      drawnItemsRef.current.addLayer(e.layer);
      const bounds = e.layer.getBounds();
      setCurrentBbox([bounds.getWest(), bounds.getSouth(), bounds.getEast(), bounds.getNorth()]);
      showStatus("✅ Area selected!", "success");
    });

    return () => map.remove();
  }, []);

  // -------------------------------------------------------
  // FETCH FARMERS
  // -------------------------------------------------------
  useEffect(() => {
    const fetchFarmers = async () => {
      try {
        const authToken = localStorage.getItem("authToken");
        if (!authToken) throw new Error("No auth token found");
        const response = await fetch(`${BASE_URL}/api/farmers/farmers/`, {
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
        });
        const data = await response.json();
        setFarmers(data);
      } catch (error) {
        showStatus("❌ Error fetching farmers: " + error.message, "error");
      }
    };
    fetchFarmers();
  }, []);

  const handleFarmerSelect = async (farmerId) => {
    setSelectedFarmer(farmerId);
    setSelectedFarm(null);
    setFarms([]);
    setCurrentBbox(null);
    drawnItemsRef.current.clearLayers();
    setFarmerDropdownOpen(false);
    if (!farmerId) return;
    try {
      const authToken = localStorage.getItem("authToken");
      const response = await fetch(`${BASE_URL}/api/farmers/get_farms_by_farmer/?farmer=${farmerId}`, {
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
      });
      let data = await response.json();
      if (!Array.isArray(data)) data = [data];
      setFarms(data);
    } catch (error) {
      showStatus("❌ Error fetching farms: " + error.message, "error");
    }
  };

  const handleFarmSelect = async (farmId) => {
    setSelectedFarm(farmId);
    setFarmDropdownOpen(false);
    try {
      const authToken = localStorage.getItem("authToken");
      const response = await fetch(`${BASE_URL}/api/farmers/farms/${farmId}/`, {
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
      });
      const farm = await response.json();
      if (!farm) return;
      const kml = JSON.parse(farm.farm_KML);
      const latlngs = kml.map(([lat, lng]) => [lat, lng]);
      drawnItemsRef.current.clearLayers();
      const polygon = L.polygon(latlngs, { color: "#00ff88", weight: 3 }).addTo(drawnItemsRef.current);
      const bounds = polygon.getBounds();
      setCurrentBbox([bounds.getWest(), bounds.getSouth(), bounds.getEast(), bounds.getNorth()]);
      mapInstance.current.fitBounds(bounds);
    } catch (error) {
      showStatus("❌ Error fetching farm details: " + error.message, "error");
    }
  };

  const selectIndex = (index) => {
    setSelectedIndex(index);
    showStatus(`Selected: ${index.toUpperCase()}`, "info");
  };

  const showStatus = (message, type = "info") => {
    setStatus({ message, type });
    if (type === "success") setTimeout(() => setStatus({ message: "", type: "info" }), 6000);
  };

  // -------------------------------------------------------
  // CORE FETCH — shared by analyzeIndex + translateReport
  // -------------------------------------------------------
  const fetchReport = async (langCode) => {
    const authToken = localStorage.getItem("authToken");
    if (!authToken) throw new Error("No auth token found");

    const response = await fetch(`${API_BASE_URL}/generate-with-ndvi/`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
      body: JSON.stringify({
        bbox: currentBbox,
        index: selectedIndex,
        farm_id: selectedFarm || 1,
        force_refresh: true,
        language: langCode,        // ← backend uses this to pick translation + TTS voice
      }),
    });

    return response.json();
  };

  // -------------------------------------------------------
  // ANALYZE (first time)
  // -------------------------------------------------------
  const analyzeIndex = async () => {
    if (!currentBbox) return showStatus("Please draw a bounding box first", "error");
    showStatus(`🛰️ Fetching ${selectedIndex.toUpperCase()} data...`, "info");

    try {
      const data = await fetchReport(selectedLang);

      if (data.success) {
        setSyncData(data.sync_data);
        setIndexData(data.index_data);
        setResultsVisible(true);
        setAudioUrl(`${BASE_URL}${data.audio_url}`);
        displayIndexOnMap(data.index_data);
        showStatus(`✅ ${data.index_type} analysis complete!`, "success");
      } else {
        showStatus("❌ Error: " + data.error, "error");
      }
    } catch (error) {
      showStatus("❌ Error: " + error.message, "error");
    }
  };

  // -------------------------------------------------------
  // TRANSLATE — switch language after analysis is done
  // -------------------------------------------------------
  const translateReport = async (langCode) => {
    setSelectedLang(langCode);
    if (!resultsVisible) return; // not yet analyzed — just set the lang for later

    setTranslating(true);
    setSyncData([]);
    setAudioUrl("");

    try {
      const data = await fetchReport(langCode);

      if (data.success) {
        setSyncData(data.sync_data);
        setAudioUrl(`${BASE_URL}${data.audio_url}`);
        const langLabel = LANGUAGES.find(l => l.code === langCode);
        showStatus(`✅ Report in ${langLabel?.flag} ${langLabel?.label}`, "success");
      } else {
        showStatus("❌ Error: " + data.error, "error");
      }
    } catch (err) {
      showStatus("❌ Error: " + err.message, "error");
    } finally {
      setTranslating(false);
    }
  };

  useEffect(() => {
    if (audioUrl && audioRef.current) {
      audioRef.current.load();
      const p = audioRef.current.play();
      if (p !== undefined) p.catch((e) => console.warn("Autoplay blocked:", e));
    }
  }, [audioUrl]);

  const displayIndexOnMap = (indexData) => {
    const map = mapInstance.current;
    if (!map || !currentBbox || !drawnItemsRef.current) return;
    if (indexImageLayerRef.current) map.removeLayer(indexImageLayerRef.current);
    const polygonLayer = drawnItemsRef.current.getLayers()[0];
    if (!polygonLayer) return;
    const latlngs = polygonLayer.getLatLngs()[0];
    const bounds = polygonLayer.getBounds();
    const topLeft = map.latLngToContainerPoint(bounds.getNorthWest());
    const bottomRight = map.latLngToContainerPoint(bounds.getSouthEast());
    const width = bottomRight.x - topLeft.x;
    const height = bottomRight.y - topLeft.y;
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    const points = latlngs.map((latlng) => {
      const point = map.latLngToContainerPoint(latlng);
      return [point.x - topLeft.x, point.y - topLeft.y];
    });
    const img = new Image();
    img.src = "data:image/png;base64," + indexData.image_base64;
    img.onload = () => {
      ctx.save();
      ctx.beginPath();
      points.forEach(([x, y], i) => (i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y)));
      ctx.closePath();
      ctx.clip();
      ctx.drawImage(img, 0, 0, width, height);
      ctx.restore();
      const overlay = L.imageOverlay(canvas.toDataURL(), bounds, { interactive: false }).addTo(map);
      indexImageLayerRef.current = overlay;
      map.fitBounds(bounds, { padding: [50, 50] });
    };
  };

  const handleTimeUpdate = (e) => {
    if (syncData.length === 0) return;
    const currentTime = e.target.currentTime;
    const wordIndex = syncData.findIndex((item, index) => {
      const nextItem = syncData[index + 1];
      const startTime = item.start_time;
      const endTime = nextItem ? nextItem.start_time : item.start_time + item.duration;
      return currentTime >= startTime && currentTime < endTime;
    });
    if (wordIndex !== -1 && wordIndex !== currentWordIndex) setCurrentWordIndex(wordIndex);
  };

  const categoryNames = {
    dense_vegetation: "Dense Vegetation",
    moderate_vegetation: "Moderate Vegetation",
    sparse_vegetation: "Sparse Vegetation",
    open_soil: "Open Soil",
  };
  const categoryClasses = {
    dense_vegetation: "category-dense",
    moderate_vegetation: "category-moderate",
    sparse_vegetation: "category-sparse",
    open_soil: "category-sparse",
  };

  const activeLang = LANGUAGES.find(l => l.code === selectedLang);

  // -------------------------------------------------------
  // RENDER
  // -------------------------------------------------------
  return (
    <div className="vegi-analysis">
      <div className="container">
        <h1>🛰️ Multi-Index Vegetation Analysis</h1>
        <div className="subtitle">NDVI • SAVI • NDMI • NDRE • RECI</div>

        {/* ---- FARMER / FARM DROPDOWNS ---- */}
        <div className="vegi-dropdown-container">
          <div className="vegi-dropdown-section">
            <label>👨‍🌾 Select Farmer:</label>
            <div className="vegi-custom-dropdown">
              <div className="vegi-selected" onClick={() => setFarmerDropdownOpen(!farmerDropdownOpen)}>
                {selectedFarmer ? farmers.find((f) => f.id === selectedFarmer)?.name : "-- Select Farmer --"}
              </div>
              {farmerDropdownOpen && (
                <>
                  <div className="vegi-dropdown-overlay" onClick={() => setFarmerDropdownOpen(false)} />
                  <div className="vegi-dropdown-modal" onClick={(e) => e.stopPropagation()}>
                    <div className="vegi-dropdown-option vegi-placeholder" onClick={() => handleFarmerSelect("")}>-- Select Farmer --</div>
                    {farmers.map((farmer) => (
                      <div key={farmer.id} className="vegi-dropdown-option" onClick={() => handleFarmerSelect(farmer.id)}>{farmer.name}</div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="vegi-dropdown-section">
            <label>🏡 Select Farm:</label>
            <div className={`vegi-custom-dropdown ${!selectedFarmer ? "vegi-disabled" : ""}`}>
              <div className="vegi-selected" onClick={() => selectedFarmer && setFarmDropdownOpen(!farmDropdownOpen)}>
                {selectedFarm ? farms.find((f) => f.id === selectedFarm)?.name : "-- Select Farm --"}
              </div>
              {farmDropdownOpen && (
                <>
                  <div className="vegi-dropdown-overlay" onClick={() => setFarmDropdownOpen(false)} />
                  <div className="vegi-dropdown-modal" onClick={(e) => e.stopPropagation()}>
                    <div className="vegi-dropdown-option vegi-placeholder" onClick={() => handleFarmSelect("")}>-- Select Farm --</div>
                    {farms.map((farm) => (
                      <div key={farm.id} className="vegi-dropdown-option" onClick={() => handleFarmSelect(farm.id)}>{farm.name}</div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* ---- INDEX SELECTOR ---- */}
        <div className="index-selector">
          <h3>📊 Select Vegetation Index</h3>
          <div className="index-buttons">
            {["ndvi", "savi", "ndmi", "ndre", "reci"].map((index) => (
              <div key={index} className={`index-btn ${selectedIndex === index ? "active" : ""}`} onClick={() => selectIndex(index)}>
                {index.toUpperCase()}
              </div>
            ))}
          </div>
        </div>

        {/* ---- LANGUAGE SELECTOR ---- */}
        <div className="index-selector">
          <h3>🌐 Voice Report Language</h3>
          <div className="index-buttons" style={{ flexWrap: "wrap", gap: "0.5rem" }}>
            {LANGUAGES.map((lang) => (
              <div
                key={lang.code}
                className={`index-btn ${selectedLang === lang.code ? "active" : ""}`}
                onClick={() => translateReport(lang.code)}
                style={{ fontSize: "0.82rem", padding: "0.45rem 1rem", opacity: translating ? 0.5 : 1, pointerEvents: translating ? "none" : "auto" }}
              >
                {lang.flag} {lang.label}
              </div>
            ))}
          </div>
          <p style={{ color: "#888", fontSize: "0.79rem", marginTop: "0.4rem" }}>
            {resultsVisible
              ? `Tap any language to retranslate the report instantly`
              : `Selected language for report: `}
            <strong style={{ color: "#00ff88" }}>{activeLang?.flag} {activeLang?.label}</strong>
          </p>
        </div>

        {/* ---- MAP ---- */}
        <div className="map-container">
          <h3 className="map-title">🗺️ Select Field Area</h3>
          <div id="map" ref={mapRef} className="map"></div>
          <div className="map-instructions">
            <strong>📍</strong> Use rectangle tool to draw field boundary
          </div>
        </div>

        {/* ---- ANALYZE BUTTON ---- */}
        <div className="action-section">
          <button onClick={analyzeIndex} disabled={!currentBbox}>
            🎤 Analyze & Generate Voice
          </button>
        </div>

        {status.message && <div className={`status ${status.type}`}>{status.message}</div>}

        {/* ---- DOMINANT CATEGORY ---- */}
        {indexData && (
          <div className="dominant-display">
            <h3>🎯 Dominant Category</h3>
            <div className={`dominant-category ${categoryClasses[indexData.dominant_category]}`}>
              {indexData.dominant_percentage}% {categoryNames[indexData.dominant_category]}
            </div>
            <p className="dominant-desc">Based on {selectedIndex.toUpperCase()} analysis</p>
          </div>
        )}

        {/* ---- RESULTS (synced text) ---- */}
        {resultsVisible && (
          <div className="results-container">
            <div className="text-display">
              {translating ? (
                <span style={{ color: "#888", fontStyle: "italic" }}>⏳ Translating to {activeLang?.flag} {activeLang?.label}...</span>
              ) : (
                syncData.map((item, index) => (
                  <span
                    key={index}
                    id={`word-${index}`}
                    className={`word ndvi-${item.color} ${currentWordIndex === index ? "active" : ""}`}
                  >
                    {item.word}{" "}
                  </span>
                ))
              )}
            </div>

            <div className="stats-panel">
              <h3>📊 Statistics</h3>
              <div className="stats-grid">
                {["dense_vegetation", "moderate_vegetation", "sparse_vegetation"].map((cat) => (
                  <div key={cat} className={`stat-card ${indexData.dominant_category === cat ? "highlight" : ""}`}>
                    <div className="stat-value">{indexData.statistics[cat].toFixed(1)}%</div>
                    <div className="stat-label">{cat.split("_")[0].toUpperCase()}</div>
                  </div>
                ))}
                <div className="stat-card">
                  <div className="stat-value">{indexData.area_acres.toFixed(1)}</div>
                  <div className="stat-label">Acres</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ---- AUDIO PLAYER ---- */}
        {audioUrl && !translating && (
          <div className="audio-player">
            <h3>
              🔊 Voice Report &nbsp;
              <span style={{ color: "#00ff88", fontSize: "0.88rem", fontWeight: "normal" }}>
                {activeLang?.flag} {activeLang?.label}
              </span>
            </h3>
            <audio ref={audioRef} src={audioUrl} controls onTimeUpdate={handleTimeUpdate} />
          </div>
        )}

      </div>
    </div>
  );
};

export default VegetationAnalysis;